import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserThunk, setCurrentUser } from '../../ducks/user.duck';

const EMPTY_DASHBOARD = {
  totalOrdersCount: 0,
  recentActivity: [],
};

export const loadCustomerDashboardThunk = createAsyncThunk(
  'CustomerDashboardPage/loadData',
  async (_, { dispatch, getState, extra: sdk, rejectWithValue }) => {
    try {
      await dispatch(fetchCurrentUserThunk({ updateNotifications: true, enforce: true })).unwrap();
    } catch (e) {
      return rejectWithValue(storableError(e));
    }

    const user = getState().user?.currentUser;
    if (!user?.id?.uuid || !sdk) {
      return EMPTY_DASHBOARD;
    }

    const processNames = getSupportedProcessesInfo().map(p => p.name);

    try {
      const [ordersMeta, recentOrders] = await Promise.all([
        sdk.transactions.query({
          only: 'order',
          processNames,
          page: 1,
          perPage: 1,
          'fields.transaction': ['lastTransition'],
        }),
        sdk.transactions.query({
          only: 'order',
          processNames,
          page: 1,
          perPage: 5,
          sort: '-lastTransitionedAt',
          include: ['listing'],
          'fields.transaction': ['lastTransition', 'lastTransitionedAt', 'processName'],
          'fields.listing': ['title'],
        }),
      ]);

      dispatch(addMarketplaceEntities(recentOrders));

      const denormalised = denormalisedResponseEntities(recentOrders);
      const recentActivity = denormalised
        .map(tx => ({
          id: tx.id.uuid,
          title: tx.listing?.attributes?.title || '',
          lastTransitionedAt: tx.attributes?.lastTransitionedAt,
        }))
        .sort((a, b) => {
          const tb = new Date(b.lastTransitionedAt || 0).getTime();
          const ta = new Date(a.lastTransitionedAt || 0).getTime();
          const vb = Number.isNaN(tb) ? 0 : tb;
          const va = Number.isNaN(ta) ? 0 : ta;
          return vb - va;
        });

      return {
        totalOrdersCount: ordersMeta.data.meta.totalItems,
        recentActivity,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const switchToSellerThunk = createAsyncThunk(
  'CustomerDashboardPage/switchToSeller',
  async ({ sellerUserType }, { dispatch, getState, extra: sdk, rejectWithValue }) => {
    const state = getState();
    const currentUser = state.user?.currentUser;
    const existingPublicData = currentUser?.attributes?.profile?.publicData || {};
    const nextPublicData = { ...existingPublicData, userType: sellerUserType };

    try {
      const response = await sdk.currentUser.updateProfile(
        { publicData: nextPublicData },
        { expand: true, include: ['effectivePermissionSet', 'profileImage'] }
      );
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      await dispatch(fetchCurrentUserThunk({ updateNotifications: true, enforce: true })).unwrap();
      return entities[0];
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  {
    condition: (_, { getState }) => {
      const page = getState().CustomerDashboardPage || {};
      return !page.switchToSellerInProgress;
    },
  }
);

const customerDashboardPageSlice = createSlice({
  name: 'CustomerDashboardPage',
  initialState: {
    loadInProgress: false,
    loadError: null,
    totalOrdersCount: null,
    recentActivity: [],
    switchToSellerInProgress: false,
    switchToSellerError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadCustomerDashboardThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
        state.totalOrdersCount = null;
        state.recentActivity = [];
      })
      .addCase(loadCustomerDashboardThunk.fulfilled, (state, action) => {
        state.loadInProgress = false;
        state.totalOrdersCount = action.payload.totalOrdersCount;
        state.recentActivity = action.payload.recentActivity;
      })
      .addCase(loadCustomerDashboardThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload || action.error;
        state.totalOrdersCount = null;
        state.recentActivity = [];
      })
      .addCase(switchToSellerThunk.pending, state => {
        state.switchToSellerInProgress = true;
        state.switchToSellerError = null;
      })
      .addCase(switchToSellerThunk.fulfilled, state => {
        state.switchToSellerInProgress = false;
      })
      .addCase(switchToSellerThunk.rejected, (state, action) => {
        state.switchToSellerInProgress = false;
        state.switchToSellerError = action.payload || action.error;
      });
  },
});

export default customerDashboardPageSlice.reducer;

export const loadData = () => dispatch => dispatch(loadCustomerDashboardThunk());
